import React, { useState, useEffect } from "react";
import { Block } from "../../../../types/sprintLessonTypes";
import { useSprintWebSocket } from "../../../context/SprintWebSocketContext";
import { useFeedbackBlock, useSetUserActionPayload, useUserActionPayload } from "../../../store/multiPageLessonStore";
import { css } from "@emotion/css";

interface TextInputQuestionBlockRendererProps {
  block: Block;
}

export const TextInputQuestionBlockRenderer: React.FC<TextInputQuestionBlockRendererProps> = ({
  block,
}) => {
  // State for user input
  const [userInput, setUserInput] = useState<string>("");
  
  const setUserActionPayload = useSetUserActionPayload();
  const userActionPayload = useUserActionPayload();

  // Subscribe to feedbackBlock changes using the block.index_key as namespace
  const feedbackBlock = useFeedbackBlock(block.index_key);

  // Get the question from block content
  const question = block.content?.question || "Please provide your answer";

  // CSS styles for the input area
  const inputContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  `;

  const textInputStyle = css`
    padding: 1rem;
    border-radius: 0.5rem;
    border: 1px solid #3d3d4d;
    background-color: #2d2d3a;
    color: #e0e0e0;
    font-family: inherit;
    font-size: 1rem;
    width: 100%;
    resize: vertical;
    min-height: 100px;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #4f46e5;
      box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.3);
    }
  `;



  // Use state to track if we need to update the payload
  const [prevInput, setPrevInput] = useState<string>("");
  
  // Update userActionPayload whenever input changes, but use prevInput to avoid infinite loop
  useEffect(() => {
    // Only update if userInput has actually changed
    if (userInput !== prevInput) {
      setPrevInput(userInput);
      
      if (userInput.trim()) {
        const payload = new Map(userActionPayload);
        payload.set(block.index_key, {
          type: "text_input_question_feedback",
          block: block,
          user_input: userInput,
          // Store feedback information so the Check button can show feedback
          showFeedback: true,
          feedbackNamespace: block.index_key,
        });
        setUserActionPayload(payload);
      }
    }
  }, [userInput, block.index_key]);

  return (
    <div>
      <div
        id={`${block.block_id}-question`}
        className={css`
          font-weight: 600;
          margin-bottom: 0.5rem;
        `}
      >
        {question}
      </div>
      <div className={inputContainerStyle}>
        <textarea
          id={`${block.block_id}-input`}
          className={textInputStyle}
          value={userInput}
          onChange={(e) => {
            const newValue = e.target.value;
            setUserInput(newValue);
          }}
          placeholder="Type your answer here..."
          aria-labelledby={`${block.block_id}-question`}
        />
      </div>
    </div>
  );
};
